import React, { useState, useRef } from 'react';
import { FileText, Plus, Upload, X, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { trackDocumentAccess } from '../../utils/analytics';
import { API } from '../../config/api';
import { formatDate } from '../../utils/dateFormat';

export function CaseDocuments({
  caseId,
  documents,
  user,
  onRefresh,
  isStaffOrAdmin
}) {
  const fileInputRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('case_id', caseId);

    try {
      const response = await fetch(`${API}/documents/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setShowUploadModal(false);
        setSelectedFile(null);
        onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Fetch with credentials to ensure authentication
      const response = await fetch(`${API}/documents/${doc.document_id}/download`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to download document');
      }
      
      // Get the blob and create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      trackDocumentAccess(doc.document_id);
      toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download document');
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'text-red-500';
      case 'doc':
      case 'docx':
        return 'text-blue-500';
      case 'xls':
      case 'xlsx':
        return 'text-green-500';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'text-purple-500';
      default:
        return 'text-gray-500';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <div className="space-y-6" data-testid="documents-content">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Documents ({documents.length})</h2>
          <Button onClick={() => setShowUploadModal(true)} className="bg-[#80298F] hover:bg-[#6b2278]">
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>

        {documents.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No documents uploaded yet</p>
            <Button 
              onClick={() => setShowUploadModal(true)}
              variant="link" 
              className="text-[#80298F] mt-2"
            >
              <Plus className="h-4 w-4 mr-1" />
              Upload first document
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="divide-y divide-gray-100">
              {documents.map(doc => (
                <div
                  key={doc.document_id}
                  className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  data-testid={`document-${doc.document_id}`}
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <FileText className={`h-8 w-8 flex-shrink-0 ${getFileIcon(doc.file_name)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{doc.file_name}</p>
                      <p className="text-sm text-gray-500">
                        {formatDate(doc.uploaded_at)} 
                        {doc.file_size && ` • ${formatFileSize(doc.file_size)}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadDocument(doc)}
                    className="flex-shrink-0 ml-4"
                    data-testid={`download-${doc.document_id}`}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upload Document Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                selectedFile ? 'border-[#80298F] bg-[#80298F]/5' : 'border-gray-300 hover:border-[#80298F]'
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
              />
              {selectedFile ? (
                <div className="flex items-center justify-center space-x-3">
                  <FileText className="h-8 w-8 text-[#80298F]" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Click to select a file</p>
                  <p className="text-sm text-gray-400 mt-1">or drag and drop</p>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUploadModal(false);
              setSelectedFile(null);
            }}>Cancel</Button>
            <Button 
              onClick={handleUploadDocument} 
              className="bg-[#80298F] hover:bg-[#6b2278]"
              disabled={!selectedFile || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CaseDocuments;
